/**
 * PO Management Dialog (Shared)
 *
 * Unified PO dialog used by both Inventory and Cashflow pages.
 * Switches between a list view (browse/filter POs) and an edit form view.
 * The edit form and charge schedule preview are extracted into sub-components.
 */

import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import MoneyOffCsredIcon from "@mui/icons-material/MoneyOffCsred";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import UndoIcon from "@mui/icons-material/Undo";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PurchaseOrder, POLineItem, WRO, WroLinkRequest, WroAllocationsRequest, WroLineAllocationsRequest, WroLineLink, OrphanedAllocation, getPOPaymentStatus, getPOLineItemsTotal } from "models/InventoryModel";
import { Supplier } from "models/CashflowModel";
import InventoryService, { CatalogSuggestions, NetSuiteDrift } from "services/InventoryService";
import POEditForm from "./POEditForm";
import ConfirmSkusDialog, { SkuQuestion } from "./ConfirmSkusDialog";
import ImportPoPdfDialog from "./ImportPoPdfDialog";
import { findLookAlikes } from "shared/utils/skuAmbiguity";
import { missingPoFields, softMissingPoFields } from "shared/utils/poCompleteness";
import { NETSUITE_STATUS_DISPLAY, NetSuiteColumnStatus } from "shared/utils/netsuiteStatus";

export interface POManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Render inline as page content instead of a modal (the Purchase Orders page) */
  standalone?: boolean;
  consolidatedPOs: PurchaseOrder[];
  inventoryOptions: Array<{ id: string; name: string | null; skus: string[] }>;
  editingPO: PurchaseOrder | null;
  isCreatingNewPO: boolean;
  editingPONumber: string;
  editingPODate: string;
  editingPODescription: string;
  editingPOLineItems: POLineItem[];
  poTabValue: "confirmed" | "draft" | "drafts" | "future" | "past" | "incomplete";
  isSavingPOs: boolean;
  isDeletingPO: boolean;
  initialFilterText?: string;
  incompletePONumbers?: Set<string>;
  onOpenEditPO: (po: PurchaseOrder) => void;
  onStartCreatePO: (prefilledInventoryId?: string) => void;
  /** Start a new PO from a PO PDF (number, date and lines read off the document) */
  onStartCreatePOFromPdf?: (po: { poNumber: string | null; poDate: string | null; lineItems: POLineItem[] }) => void;
  /** Start a new PO as a copy of this one */
  onDuplicatePO: (po: PurchaseOrder) => void;
  /** Duplicate the PO open in the editor, as it is on screen */
  onDuplicateOpenPO: () => void;
  duplicatedFromPoNumber?: string | null;
  onCancelEdit: () => void;
  onSavePO: () => void;
  onDeletePO: (po: PurchaseOrder) => void;
  onSetPONumber: (value: string) => void;
  onSetPODate: (value: string) => void;
  onSetPODescription: (value: string) => void;
  onAddLineItem: () => void;
  onUpdateLineItem: (index: number, updates: Partial<POLineItem>) => void;
  onRemoveLineItem: (index: number) => void;
  /** Swap the whole line list (PDF import, match-to-WRO) */
  onReplaceLineItems?: (items: POLineItem[]) => void;
  onSetPOTabValue: (value: "confirmed" | "draft" | "drafts" | "future" | "past" | "incomplete") => void;

  // PO workflow action callbacks
  onPromotePO?: (po: PurchaseOrder) => void;
  onMarkReceived?: (po: PurchaseOrder) => Promise<void>;
  onUnmarkReceived?: (po: PurchaseOrder) => Promise<void>;
  onBulkMarkReceived?: (poNumbers: string[]) => Promise<void>;
  onBulkUnmarkReceived?: (poNumbers: string[]) => Promise<void>;
  onBulkMarkAllPaid?: (updates: Array<{ poNumber: string; paidTranches: Record<string, boolean> }>) => Promise<void>;
  onBulkClearPaid?: (poNumbers: string[]) => Promise<void>;
  onTogglePaidTranche?: (poNumber: string, trancheOrder: number, paid: boolean) => Promise<void>;
  isUpdatingPaidStatus?: boolean;

  // ShipBob WRO link (Inventory page only)
  wroOptions?: WRO[];
  onLinkWRO?: (poNumber: string, link: WroLinkRequest) => Promise<void>;
  /** Replace which WRO lines each line of the PO draws on (the allocation editor) */
  onSetWroAllocations?: (poNumber: string, request: WroAllocationsRequest) => Promise<void>;
  /** Replace every allocation on one WRO, for every PO (edited from whichever PO is open) */
  onSetWroLineAllocations?: (wroId: string, lines: WroLineAllocationsRequest["lines"]) => Promise<void>;
  /** A supplier was added from the editor; the page keeps the list */
  onSuppliersChanged?: (suppliers: Supplier[]) => void;
  onWroCreated?: (wro: { wroId: string; fulfillmentCenterName: string | null }) => void;
  onNetSuitePushed?: (result: { netsuitePoId: string; tranId: string; url: string }) => void;

  // Cashflow-specific props (all optional)
  suppliers?: Supplier[];
  editingPOAmount?: number | null;
  editingPOSupplierId?: number | null;
  editingPOTariffRate?: number | null;
  editingPOStatus?: 'placed' | 'forecasted' | 'draft';
  editingPOBrand?: string | null;
  editingPOPlacementDate?: string | null;
  editingPOEtd?: string | null;
  onSetPOAmount?: (value: number | null) => void;
  onSetPOSupplierId?: (value: number | null) => void;
  onSetPOTariffRate?: (value: number | null) => void;
  onSetPOStatus?: (value: 'placed' | 'forecasted' | 'draft') => void;
  onSetPOBrand?: (value: string | null) => void;
  onSetPOPlacementDate?: (value: string | null) => void;
  onSetPOEtd?: (value: string | null) => void;
  editingPOCountryOfDestination?: string | null;
  editingPOPaymentDateOverrides?: Record<string, string> | null;
  onSetPOCountryOfDestination?: (value: string | null) => void;
  onSetPOPaymentDateOverrides?: (value: Record<string, string> | null) => void;
  editingPOPriceType?: string | null;
  editingPOContainerType?: string | null;
  editingPOShipToFcId?: number | null;
  onSetPOPriceType?: (value: string | null) => void;
  onSetPOContainerType?: (value: string | null) => void;
  onSetPOShipToFcId?: (value: number | null) => void;
  catalogSuggestions?: CatalogSuggestions | null;
  onSuggestPONumber?: () => void;
  /** Generates and opens the PO document PDF for a PO row */
  onGeneratePdf?: (po: PurchaseOrder) => void;
  generatingPdfPoNumber?: string | null;
}

export default function POManagementDialog({
  isOpen,
  onClose,
  standalone = false,
  consolidatedPOs,
  inventoryOptions,
  editingPO,
  isCreatingNewPO,
  editingPONumber,
  editingPODate,
  editingPODescription,
  editingPOLineItems,
  poTabValue,
  isSavingPOs,
  isDeletingPO,
  initialFilterText,
  onOpenEditPO,
  onStartCreatePO,
  onStartCreatePOFromPdf,
  onDuplicatePO,
  onDuplicateOpenPO,
  duplicatedFromPoNumber = null,
  onCancelEdit,
  onSavePO,
  onDeletePO,
  onSetPONumber,
  onSetPODate,
  onSetPODescription,
  onAddLineItem,
  onUpdateLineItem,
  onRemoveLineItem,
  onReplaceLineItems,
  onSetPOTabValue,
  incompletePONumbers,
  // PO workflow actions
  onPromotePO,
  onMarkReceived,
  onUnmarkReceived,
  wroOptions = [],
  onLinkWRO,
  onSetWroAllocations,
  onSetWroLineAllocations,
  onSuppliersChanged,
  onWroCreated,
  onNetSuitePushed,
  onBulkMarkReceived,
  onBulkUnmarkReceived,
  onBulkMarkAllPaid,
  onBulkClearPaid,
  onTogglePaidTranche,
  isUpdatingPaidStatus,
  // Cashflow props
  suppliers = [],
  editingPOAmount,
  editingPOSupplierId,
  editingPOTariffRate,
  editingPOStatus = 'placed',
  editingPOBrand,
  editingPOPlacementDate,
  editingPOEtd,
  onSetPOAmount,
  onSetPOSupplierId,
  onSetPOTariffRate,
  onSetPOStatus,
  onSetPOBrand,
  onSetPOPlacementDate,
  onSetPOEtd,
  editingPOCountryOfDestination,
  editingPOPaymentDateOverrides,
  onSetPOCountryOfDestination,
  onSetPOPaymentDateOverrides,
  editingPOPriceType,
  editingPOContainerType,
  editingPOShipToFcId,
  onSetPOPriceType,
  onSetPOContainerType,
  onSetPOShipToFcId,
  catalogSuggestions,
  onSuggestPONumber,
  onGeneratePdf,
  generatingPdfPoNumber,
}: POManagementDialogProps): JSX.Element {
  // POs looked up in NetSuite, including externally created orders — flags rows and explains the
  // gap in the editor. Re-checked whenever the PO list changes (a save, a push).
  const inventoryService = useMemo(() => new InventoryService(), []);
  const [netsuiteDrift, setNetsuiteDrift] = useState<NetSuiteDrift>({});
  const [netsuiteDriftLoaded, setNetsuiteDriftLoaded] = useState(false);
  const [netsuiteDriftError, setNetsuiteDriftError] = useState<string | null>(null);
  const poLookupKey = consolidatedPOs.map(po => `${po.poNumber}:${po.netsuitePushedAt ?? ''}`).join('|');
  const refreshNetSuiteDrift = React.useCallback(() => {
    setNetsuiteDriftLoaded(false);
    setNetsuiteDriftError(null);
    inventoryService.getNetSuiteDrift()
      .then(setNetsuiteDrift)
      .catch(err => {
        console.error("NetSuite drift check failed:", err);
        setNetsuiteDriftError(err?.message || String(err));
      })
      .finally(() => setNetsuiteDriftLoaded(true));
  }, [inventoryService]);
  useEffect(() => {
    if ((!isOpen && !standalone) || !poLookupKey) return;
    refreshNetSuiteDrift();
  }, [isOpen, standalone, poLookupKey, refreshNetSuiteDrift]);

  // Create a PO from its PDF: the Import button, or a PDF dropped anywhere on the list.
  const [pdfImport, setPdfImport] = useState<{ open: boolean; file: File | null }>({ open: false, file: null });
  const [isPdfDragOver, setIsPdfDragOver] = useState(false);
  const listAcceptsPdfDrop = Boolean(onStartCreatePOFromPdf) && !editingPO && !isCreatingNewPO;
  const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
  const pdfDropProps = listAcceptsPdfDrop ? {
    onDragOver: (e: React.DragEvent) => { if (!isFileDrag(e)) return; e.preventDefault(); setIsPdfDragOver(true); },
    onDragLeave: (e: React.DragEvent) => { if (e.currentTarget.contains(e.relatedTarget as Node)) return; setIsPdfDragOver(false); },
    onDrop: (e: React.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      setIsPdfDragOver(false);
      const dropped = e.dataTransfer.files?.[0];
      if (dropped) setPdfImport({ open: true, file: dropped });
    },
    style: { position: "relative" as const },
  } : {};

  // SKU confirmation: lines whose item has look-alikes and was not yet confirmed by a
  // person. Lines matching the linked ShipBob WRO are exempt (ShipBob is the truth).
  const linkedWroItemIds = useMemo(() => {
    if (!editingPO) return new Set<string>();
    const current = consolidatedPOs.find(p => p.poNumber === editingPO.poNumber);
    const ids = new Set((current?.lineItems ?? []).flatMap(li => li.wroIds ?? []));
    return new Set(wroOptions.filter(w => ids.has(w.wroId)).flatMap(w => w.lines.map(l => String(l.inventoryId))));
  }, [editingPO, consolidatedPOs, wroOptions]);
  const skuQuestions: SkuQuestion[] = useMemo(() => {
    const questions: SkuQuestion[] = [];
    editingPOLineItems.forEach((line, index) => {
      if (!line.inventoryId || line.quantity < 1) return;
      if (line.skuConfirmed || (line.skuConfirmed !== false && line.skuConfirmedAt)) return;
      if (linkedWroItemIds.has(String(line.inventoryId))) return;
      const current = inventoryOptions.find(opt => opt.id === line.inventoryId);
      if (!current) return;
      const lookAlikes = findLookAlikes(line.inventoryId, inventoryOptions, catalogSuggestions?.manufacturerSkus);
      if (lookAlikes.length > 0) questions.push({ index, line, current, lookAlikes });
    });
    return questions;
  }, [editingPOLineItems, inventoryOptions, catalogSuggestions, linkedWroItemIds]);
  const [confirmSkusMode, setConfirmSkusMode] = useState<null | "review" | "save">(null);
  const [saveAfterConfirm, setSaveAfterConfirm] = useState(false);
  // The confirmed lines land in state first; save on the next render so it sees them
  useEffect(() => {
    if (!saveAfterConfirm) return;
    setSaveAfterConfirm(false);
    onSavePO();
  }, [saveAfterConfirm, onSavePO]);
  const applySkuChoices = (choices: Record<number, string>) => {
    for (const q of skuQuestions) {
      const chosen = choices[q.index] ?? q.current.id;
      const opt = inventoryOptions.find(o => o.id === chosen);
      onUpdateLineItem(q.index, {
        inventoryId: chosen,
        inventoryName: opt?.name ?? undefined,
        skuConfirmed: true,
      });
    }
  };
  // Required before a PO is saved as placed/forecasted; a draft skips this (and the SKU check)
  const editorMissing = missingPoFields({
    lineItems: editingPOLineItems,
    containerType: editingPOContainerType,
    shipToFcId: editingPOShipToFcId,
  });
  const isDraft = editingPOStatus === 'draft';
  const handleSaveClick = () => {
    if (isDraft) { onSavePO(); return; }
    if (editorMissing.length > 0) return;
    if (skuQuestions.length > 0) setConfirmSkusMode("save");
    else onSavePO();
  };
  // Status flips land in state first; the save runs on the next render so it sees them
  const saveWithStatus = (status: 'placed' | 'draft') => {
    onSetPOStatus?.(status);
    setSaveAfterConfirm(true);
  };

  const [sortField, setSortField] = useState<"poNumber" | "arriveByDate">("arriveByDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [searchText, setSearchText] = useState("");
  const [filterDateStart, setFilterDateStart] = useState<string | null>(null);
  const [filterDateEnd, setFilterDateEnd] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedPOs, setSelectedPOs] = useState<Set<string>>(new Set());
  const [isMarkingReceived, setIsMarkingReceived] = useState(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'paid' | 'partial' | 'unpaid'>('all');

  // Sync initialFilterText into searchText whenever dialog opens
  useEffect(() => {
    if (isOpen) {
      setSearchText(initialFilterText || "");
      setFilterDateStart(null);
      setFilterDateEnd(null);
      setCurrentPage(0);
      setSelectedPOs(new Set());
      setPaymentFilter('all');
    }
  }, [isOpen, initialFilterText]);

  const handleSort = (field: "poNumber" | "arriveByDate") => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Build SKU lookup map for O(1) lookups
  const skusByInventoryId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const opt of inventoryOptions) {
      map.set(opt.id, opt.skus);
    }
    return map;
  }, [inventoryOptions]);

  const today = moment();

  // Only a receipt a person marked can be unmarked; one ShipBob derived from stowed units stands
  // until the WRO link changes. The header flag is an AND across lines, so requiring it too keeps
  // "unmark" off a PO that is only partly hand-received and therefore shows no received chip.
  const hasHandReceipt = (po: PurchaseOrder) =>
    po.received === true && po.lineItems.some(li => li.receivedSource === 'manual');
  // Drafts (status 'draft') are POs still being filled in: they sit on their own tab and
  // count nowhere else — not in Confirmed/Forecasted/Future/Past, not in any metric.
  const unfinishedPOs = consolidatedPOs.filter(po => po.status === 'draft');
  const livePOs = consolidatedPOs.filter(po => po.status !== 'draft');
  const futurePOs = livePOs.filter(po => moment(po.arriveByDate).isSameOrAfter(today, "day"));
  const pastPOs = livePOs.filter(po => moment(po.arriveByDate).isBefore(today, "day"));
  const confirmedPOs = livePOs.filter(po => po.status === 'placed' || !po.status);
  const draftPOs = livePOs.filter(po => po.status === 'forecasted');
  // Placed/forecasted POs not yet in NetSuite that still lack required fields
  const missingFieldsByPo = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const po of livePOs) {
      if (po.netsuitePoId || (netsuiteDrift[po.poNumber] && !netsuiteDrift[po.poNumber].missing)) continue;
      // Required fields first, then reminders (tariff) that don't block anything
      const missing = [...missingPoFields(po), ...softMissingPoFields(po)];
      if (missing.length > 0) map.set(po.poNumber, missing);
    }
    return map;
  }, [livePOs, netsuiteDrift]);

  const incompletePOs = useMemo(() => {
    if (!incompletePONumbers || incompletePONumbers.size === 0) return [];
    return consolidatedPOs.filter(po => incompletePONumbers.has(po.poNumber));
  }, [consolidatedPOs, incompletePONumbers]);

  // Supplier tranche count lookup for payment status derivation
  const supplierTrancheCount = useMemo(() => {
    const map = new Map<number, number>();
    for (const s of suppliers) {
      map.set(s.id, s.paymentTerms?.length || 0);
    }
    return map;
  }, [suppliers]);

  const getPaymentStatus = (po: PurchaseOrder) => {
    const trancheCount = po.supplierId ? (supplierTrancheCount.get(po.supplierId) || 0) : 0;
    return getPOPaymentStatus(po.paidTranches, trancheCount);
  };

  // Filter pipeline
  const filteredPOs = useMemo(() => {
    const tabFiltered = poTabValue === "confirmed" ? confirmedPOs
      : poTabValue === "draft" ? draftPOs
      : poTabValue === "drafts" ? unfinishedPOs
      : poTabValue === "incomplete" ? incompletePOs
      : poTabValue === "future" ? futurePOs
      : pastPOs;

    const lowerSearch = searchText.trim().toLowerCase();
    const textFiltered = lowerSearch
      ? tabFiltered.filter(po => {
          if (po.poNumber.toLowerCase().includes(lowerSearch)) return true;
          if (po.description && po.description.toLowerCase().includes(lowerSearch)) return true;
          return po.lineItems.some(li => {
            if (li.inventoryId.toLowerCase().includes(lowerSearch)) return true;
            if (li.inventoryName && li.inventoryName.toLowerCase().includes(lowerSearch)) return true;
            const skus = skusByInventoryId.get(li.inventoryId) || [];
            return skus.some(sku => sku.toLowerCase().includes(lowerSearch));
          });
        })
      : tabFiltered;

    const dateFiltered = (filterDateStart || filterDateEnd)
      ? textFiltered.filter(po => {
          if (filterDateStart && po.arriveByDate < filterDateStart) return false;
          if (filterDateEnd && po.arriveByDate > filterDateEnd) return false;
          return true;
        })
      : textFiltered;

    const paymentFiltered = paymentFilter === 'all'
      ? dateFiltered
      : dateFiltered.filter(po => getPaymentStatus(po) === (paymentFilter === 'partial' ? 'partially-paid' : paymentFilter));

    const sorted = [...paymentFiltered].sort((a, b) => {
      const aVal = sortField === "poNumber" ? a.poNumber : a.arriveByDate;
      const bVal = sortField === "poNumber" ? b.poNumber : b.arriveByDate;
      const cmp = aVal.localeCompare(bVal);
      return sortDirection === "asc" ? cmp : -cmp;
    });

    return sorted;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consolidatedPOs, poTabValue, searchText, filterDateStart, filterDateEnd, paymentFilter, sortField, sortDirection, skusByInventoryId, supplierTrancheCount]);

  const totalFilteredCount = filteredPOs.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredCount / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages - 1);
  const paginatedPOs = filteredPOs.slice(safePage * itemsPerPage, safePage * itemsPerPage + itemsPerPage);

  const hasActiveFilters = searchText.trim() !== "" || filterDateStart !== null || filterDateEnd !== null || paymentFilter !== 'all';

  const handleClearFilters = () => {
    setSearchText("");
    setFilterDateStart(null);
    setFilterDateEnd(null);
    setCurrentPage(0);
  };

  const handleStartCreatePO = () => {
    const matchedInventory = inventoryOptions.find(opt => opt.id === searchText.trim());
    onStartCreatePO(matchedInventory?.id);
  };

  // Per-PO max order amount validation
  const selectedSupplier = suppliers.find(s => s.id === editingPOSupplierId);
  const exceedsMaxOrder = selectedSupplier?.maxOrderAmount != null
    && editingPOAmount != null
    && editingPOAmount > selectedSupplier.maxOrderAmount;

  // PO numbers must be unique: the upsert key is (inventory_id, po_number), so
  // reusing one silently merges the new PO into the existing one instead of
  // failing. Compared trimmed and case-folded, because "208 UK " and "208 UK"
  // are the same PO to everyone reading the document. The server enforces this
  // too, for the case where two people create a PO at the same moment.
  const normalizePONumber = (value: string) => value.trim().toUpperCase();
  const poNumberConflict = Boolean(editingPONumber.trim())
    && normalizePONumber(editingPO?.poNumber ?? "") !== normalizePONumber(editingPONumber)
    && consolidatedPOs.some(po => normalizePONumber(po.poNumber) === normalizePONumber(editingPONumber));

  const isFormValid = editingPONumber &&
    editingPOLineItems.length > 0 &&
    !editingPOLineItems.some(li => !li.inventoryId || li.quantity <= 0) &&
    !exceedsMaxOrder &&
    !poNumberConflict;

  // Supplier name lookup
  const supplierMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of suppliers) {
      map.set(s.id, s.name);
    }
    return map;
  }, [suppliers]);

  // Bulk selection helpers
  const showBulkActions = !!(onBulkMarkReceived || onBulkUnmarkReceived || onBulkMarkAllPaid || onBulkClearPaid);
  const allPageSelected = paginatedPOs.length > 0 && paginatedPOs.every(po => selectedPOs.has(po.poNumber));
  const somePageSelected = paginatedPOs.some(po => selectedPOs.has(po.poNumber));

  const handleToggleSelectAll = () => {
    setSelectedPOs(prev => {
      const next = new Set(prev);
      if (allPageSelected) {
        for (const po of paginatedPOs) next.delete(po.poNumber);
      } else {
        for (const po of paginatedPOs) next.add(po.poNumber);
      }
      return next;
    });
  };

  const handleToggleSelect = (poNumber: string) => {
    setSelectedPOs(prev => {
      const next = new Set(prev);
      if (next.has(poNumber)) next.delete(poNumber);
      else next.add(poNumber);
      return next;
    });
  };

  const handleBulkMarkReceived = async () => {
    const poNumbers = filteredPOs
      .filter(po =>
        selectedPOs.has(po.poNumber) &&
        po.received !== true &&
        moment(po.arriveByDate).isSameOrBefore(today, "day")
      )
      .map(po => po.poNumber);
    if (poNumbers.length > 0 && onBulkMarkReceived) {
      setIsMarkingReceived(true);
      try {
        await onBulkMarkReceived(poNumbers);
      } finally {
        setIsMarkingReceived(false);
      }
    }
    setSelectedPOs(new Set());
  };

  const handleBulkUnmarkReceived = async () => {
    const poNumbers = filteredPOs
      .filter(po =>
        selectedPOs.has(po.poNumber) && hasHandReceipt(po)
      )
      .map(po => po.poNumber);
    if (poNumbers.length > 0 && onBulkUnmarkReceived) {
      setIsMarkingReceived(true);
      try {
        await onBulkUnmarkReceived(poNumbers);
      } finally {
        setIsMarkingReceived(false);
      }
    }
    setSelectedPOs(new Set());
  };

  // Counts for enabling/disabling bulk buttons
  const selectedUnreceivedPastDueCount = filteredPOs.filter(po =>
    selectedPOs.has(po.poNumber) &&
    po.received !== true &&
    moment(po.arriveByDate).isSameOrBefore(today, "day")
  ).length;

  const selectedReceivedCount = filteredPOs.filter(po =>
    selectedPOs.has(po.poNumber) && hasHandReceipt(po)
  ).length;

  const handleBulkMarkAllPaid = async () => {
    const updates = filteredPOs
      .filter(po => selectedPOs.has(po.poNumber) && po.supplierId)
      .map(po => {
        const count = supplierTrancheCount.get(po.supplierId!) || 0;
        const paidTranches: Record<string, boolean> = {};
        for (let i = 1; i <= count; i++) paidTranches[String(i)] = true;
        return { poNumber: po.poNumber, paidTranches };
      })
      .filter(u => Object.keys(u.paidTranches).length > 0);
    if (updates.length > 0 && onBulkMarkAllPaid) {
      setIsMarkingPaid(true);
      try {
        await onBulkMarkAllPaid(updates);
      } finally {
        setIsMarkingPaid(false);
      }
    }
    setSelectedPOs(new Set());
  };

  const handleBulkClearPaid = async () => {
    const poNumbers = filteredPOs
      .filter(po => selectedPOs.has(po.poNumber) && po.paidTranches && Object.values(po.paidTranches).some(Boolean))
      .map(po => po.poNumber);
    if (poNumbers.length > 0 && onBulkClearPaid) {
      setIsMarkingPaid(true);
      try {
        await onBulkClearPaid(poNumbers);
      } finally {
        setIsMarkingPaid(false);
      }
    }
    setSelectedPOs(new Set());
  };

  const selectedNotFullyPaidCount = filteredPOs.filter(po =>
    selectedPOs.has(po.poNumber) && getPaymentStatus(po) !== 'paid'
  ).length;

  const selectedHasPaidCount = filteredPOs.filter(po =>
    selectedPOs.has(po.poNumber) && po.paidTranches && Object.values(po.paidTranches).some(Boolean)
  ).length;

  const dialogInner = (
    <>
      <DialogTitle>
        {editingPO ? "Edit Purchase Order" : isCreatingNewPO ? (duplicatedFromPoNumber ? `Create Purchase Order — copy of ${duplicatedFromPoNumber}` : "Create Purchase Order") : "Manage Purchase Orders"}
      </DialogTitle>
      <DialogContent {...pdfDropProps} sx={{
        position: "relative",
        ...((editingPO || isCreatingNewPO) && {
          backgroundColor: editingPOStatus === 'placed' ? 'rgba(33, 150, 243, 0.03)' : 'rgba(255, 167, 38, 0.04)',
          transition: 'background-color 0.3s ease',
        }),
      }}>
        {isPdfDragOver && (
          <Box sx={{
            position: "absolute", inset: 0, zIndex: 5, display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(238, 242, 255, 0.92)", border: "3px dashed", borderColor: "primary.main", borderRadius: 2, pointerEvents: "none",
          }}>
            <Typography variant="h6" color="primary">Drop the PO PDF to create a new PO from it</Typography>
          </Box>
        )}
        {/* Loading overlay when deleting */}
        {isDeletingPO && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Deleting purchase order...
            </Typography>
          </Box>
        )}

        {!editingPO && !isCreatingNewPO ? (
          // List view of all consolidated POs
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Tabs
                value={poTabValue}
                onChange={(_, newValue) => {
                  onSetPOTabValue(newValue);
                  setCurrentPage(0);
                  setSelectedPOs(new Set());
                }}
                sx={{ minHeight: 36 }}
              >
                <Tab
                  label={`Confirmed (${confirmedPOs.length})`}
                  value="confirmed"
                  sx={{ minHeight: 36, py: 0 }}
                />
                <Tab
                  label={`Forecasted (${draftPOs.length})`}
                  value="draft"
                  sx={{ minHeight: 36, py: 0 }}
                />
                <Tab
                  label={`Drafts (${unfinishedPOs.length})`}
                  value="drafts"
                  sx={{ minHeight: 36, py: 0 }}
                />
                <Tab
                  label={`Future (${futurePOs.length})`}
                  value="future"
                  sx={{ minHeight: 36, py: 0 }}
                />
                <Tab
                  label={`Past (${pastPOs.length})`}
                  value="past"
                  sx={{ minHeight: 36, py: 0 }}
                />
                {incompletePONumbers && incompletePONumbers.size > 0 && (
                  <Tab
                    label={`Incomplete (${incompletePOs.length})`}
                    value="incomplete"
                    sx={{ minHeight: 36, py: 0 }}
                    icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                  />
                )}
              </Tabs>
              <Box sx={{ display: "flex", gap: 1 }}>
                {onStartCreatePOFromPdf && (
                  <Tooltip title="Read the PO number, date and line items off a PO PDF (or just drop the PDF anywhere on this list)">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => setPdfImport({ open: true, file: null })}
                    >
                      Import from PDF
                    </Button>
                  </Tooltip>
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleStartCreatePO}
                >
                  Create New PO
                </Button>
              </Box>
            </Box>

            {/* Filter + Pagination row */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="Search POs"
                placeholder="Search by PO#, inventory ID, name, or SKU..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(0);
                }}
                sx={{ minWidth: 200, flex: 1, maxWidth: 300 }}
              />
              <DatePicker
                label="Date From"
                value={filterDateStart ? moment(filterDateStart) : null}
                onChange={(newValue: any) => {
                  setFilterDateStart(newValue && newValue.isValid() ? newValue.format("YYYY-MM-DD") : null);
                  setCurrentPage(0);
                }}
                slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
              />
              <DatePicker
                label="Date To"
                value={filterDateEnd ? moment(filterDateEnd) : null}
                onChange={(newValue: any) => {
                  setFilterDateEnd(newValue && newValue.isValid() ? newValue.format("YYYY-MM-DD") : null);
                  setCurrentPage(0);
                }}
                slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
              />
              {(onBulkMarkAllPaid || onBulkClearPaid) && (
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Payment</InputLabel>
                  <Select
                    value={paymentFilter}
                    label="Payment"
                    onChange={(e) => {
                      setPaymentFilter(e.target.value as any);
                      setCurrentPage(0);
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="partial">Partial</MenuItem>
                    <MenuItem value="unpaid">Unpaid</MenuItem>
                  </Select>
                </FormControl>
              )}
              {hasActiveFilters && (
                <Button size="small" variant="outlined" onClick={() => {
                  handleClearFilters();
                  setPaymentFilter('all');
                }}>
                  Clear Filters
                </Button>
              )}

              {totalFilteredCount > 0 && (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", ml: "auto" }}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {totalFilteredCount} PO{totalFilteredCount !== 1 ? "s" : ""} • Page {safePage + 1} / {totalPages}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setCurrentPage(Math.max(0, safePage - 1))}
                    disabled={safePage <= 0}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <InputLabel>Page</InputLabel>
                    <Select
                      value={safePage}
                      label="Page"
                      onChange={(e) => setCurrentPage(Number(e.target.value))}
                    >
                      {Array.from({ length: totalPages }).map((_, i) => (
                        <MenuItem key={i} value={i}>{i + 1}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    size="small"
                    onClick={() => setCurrentPage(Math.min(totalPages - 1, safePage + 1))}
                    disabled={safePage >= totalPages - 1}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            {showBulkActions && paginatedPOs.length > 0 && (
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                <Checkbox
                  size="small"
                  checked={allPageSelected}
                  indeterminate={somePageSelected && !allPageSelected}
                  onChange={handleToggleSelectAll}
                />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {selectedPOs.size > 0 ? `${selectedPOs.size} selected` : "Select all"}
                </Typography>
                {onBulkMarkReceived && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    disabled={selectedUnreceivedPastDueCount === 0 || isMarkingReceived}
                    onClick={handleBulkMarkReceived}
                    startIcon={isMarkingReceived ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    Mark Received ({selectedUnreceivedPastDueCount})
                  </Button>
                )}
                {onBulkUnmarkReceived && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={selectedReceivedCount === 0 || isMarkingReceived}
                    onClick={handleBulkUnmarkReceived}
                    startIcon={isMarkingReceived ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    Mark Unreceived ({selectedReceivedCount})
                  </Button>
                )}
                {onBulkMarkAllPaid && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    disabled={selectedNotFullyPaidCount === 0 || isMarkingPaid}
                    onClick={handleBulkMarkAllPaid}
                    startIcon={isMarkingPaid ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    Mark All Paid ({selectedNotFullyPaidCount})
                  </Button>
                )}
                {onBulkClearPaid && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={selectedHasPaidCount === 0 || isMarkingPaid}
                    onClick={handleBulkClearPaid}
                    startIcon={isMarkingPaid ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    Mark All Unpaid ({selectedHasPaidCount})
                  </Button>
                )}
                {selectedPOs.size > 0 && (
                  <Button size="small" onClick={() => setSelectedPOs(new Set())}>
                    Clear Selection
                  </Button>
                )}
              </Box>
            )}

            {paginatedPOs.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {showBulkActions && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={allPageSelected}
                            indeterminate={somePageSelected && !allPageSelected}
                            onChange={handleToggleSelectAll}
                          />
                        </TableCell>
                      )}
                      <TableCell sortDirection={sortField === "poNumber" ? sortDirection : false}>
                        <TableSortLabel
                          active={sortField === "poNumber"}
                          direction={sortField === "poNumber" ? sortDirection : "asc"}
                          onClick={() => handleSort("poNumber")}
                        >
                          PO Number
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={sortField === "arriveByDate" ? sortDirection : false}>
                        <TableSortLabel
                          active={sortField === "arriveByDate"}
                          direction={sortField === "arriveByDate" ? sortDirection : "asc"}
                          onClick={() => handleSort("arriveByDate")}
                        >
                          Arrive By Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>NetSuite</TableCell>
                      <TableCell align="right">Line Items</TableCell>
                      <TableCell align="right">Total Qty</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedPOs.map((po) => {
                      const isIncomplete = incompletePONumbers?.has(po.poNumber);
                      return (
                      <TableRow
                        key={po.poNumber}
                        hover
                        sx={{ cursor: 'pointer' }}
                        // Anywhere on the row opens the PO, except the controls that do their own thing
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          if (target.closest('button, a, input, [role="button"], [role="checkbox"]')) return;
                          onOpenEditPO(po);
                        }}
                      >
                        {showBulkActions && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={selectedPOs.has(po.poNumber)}
                              onChange={() => handleToggleSelect(po.poNumber)}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isIncomplete && <WarningAmberIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
                            <Typography
                              variant="body2"
                              component="button"
                              onClick={() => onOpenEditPO(po)}
                              sx={{
                                fontWeight: 600, color: 'primary.main', background: 'none', border: 0, p: 0,
                                cursor: 'pointer', font: 'inherit', textAlign: 'left',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {po.poNumber}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {po.arriveByDate}
                          {po.status !== 'forecasted' && po.received !== true
                            && po.projectedArrivalDate && po.projectedArrivalDate !== po.arriveByDate && (
                            <Tooltip title={`Overdue and not received, so it stays open and the forecast expects it ${po.projectedArrivalDate}, based on where ShipBob last reported the WRO. The date moves out on its own until ShipBob has the goods. Edit the date to override.`}>
                              <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', cursor: 'help' }}>
                                overdue · expects {po.projectedArrivalDate}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>{po.description}</TableCell>
                        <TableCell>
                          {po.supplierId ? supplierMap.get(po.supplierId) || '-' : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {po.amount != null
                            ? `$${Number(po.amount).toLocaleString()}`
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={po.status || 'placed'}
                            color={po.status === 'forecasted' ? 'default' : 'primary'}
                            variant="outlined"
                          />
                          {missingFieldsByPo.has(po.poNumber) && (
                            <Tooltip title={`Not in NetSuite yet and still missing: ${missingFieldsByPo.get(po.poNumber)!.join(', ')}. Open the PO to fill them in.`}>
                              <Chip size="small" label="Fields missing" color="warning" variant="outlined" sx={{ ml: 0.5, cursor: 'help' }} />
                            </Tooltip>
                          )}
                          {(() => {
                            const linkedLines = po.lineItems.filter(li => li.wroIds && li.wroIds.length > 0);
                            if (linkedLines.length === 0) {
                              // Pinned to nothing: auto-match is off for this PO, which otherwise
                              // looks exactly like "no WRO matched yet".
                              if (po.wroLinkSource !== 'manual') return null;
                              return (
                                <Tooltip title="Auto-match turned off by hand: no ShipBob WRO counts for this PO. Pick one, or resume auto-match, in the edit form.">
                                  <Chip size="small" label="WRO · off" color="warning" variant="outlined" sx={{ ml: 0.5, cursor: 'help' }} />
                                </Tooltip>
                              );
                            }
                            const wroIds = Array.from(new Set(linkedLines.flatMap(li => li.wroIds ?? [])));
                            const statuses = Array.from(new Set(linkedLines.flatMap(li => (li.wroStatus ?? '').split('/')).filter(Boolean)));
                            const allStowed = linkedLines.every(li => li.receivedSource === 'wro');
                            // Quantities can drift when the WRO is edited in ShipBob directly.
                            // Flag it while the WRO is still open (completed shortfalls are
                            // handled by the Missing units / claims flow).
                            const wroOpen = !statuses.every(status => status === 'Completed');
                            const driftedLines = wroOpen
                              ? linkedLines.filter(li => li.wroExpectedQuantity != null && li.wroExpectedQuantity !== li.quantity)
                              : [];
                            return (
                              <>
                              {driftedLines.length > 0 && (
                                <Tooltip
                                  title={
                                    <Box sx={{ whiteSpace: 'pre-line' }}>
                                      {`WRO ${wroIds.join(', ')} quantities differ from this PO — it may have been edited in ShipBob:\n`}
                                      {driftedLines.map(li => `${li.inventoryName || li.inventoryId}: PO ${li.quantity} · WRO ${li.wroExpectedQuantity}`).join('\n')}
                                      {'\nOpen the PO to match it to the WRO or update the WRO.'}
                                    </Box>
                                  }
                                >
                                  <Chip size="small" label="WRO qty ≠ PO" color="warning" sx={{ ml: 0.5, cursor: 'help' }} />
                                </Tooltip>
                              )}
                              <Tooltip
                                title={
                                  <Box sx={{ whiteSpace: 'pre-line' }}>
                                    {`ShipBob WRO ${wroIds.join(', ')} (${linkedLines.some(li => (li.wroLinks ?? []).some(l => l.linkKind === 'allocation')) ? 'allocated by hand' : 'matched by PO number'}${po.wroLinkSource === 'manual' ? '; auto-match off' : ''})\n`}
                                    {linkedLines.map(li =>
                                      `${li.inventoryName || li.inventoryId}: ${li.stowedQuantity ?? 0}/${li.quantity} stowed${(li.wroExpectedQuantity ?? li.quantity) !== li.quantity ? ` (WRO expects ${li.wroExpectedQuantity})` : ''}`
                                    ).join('\n')}
                                  </Box>
                                }
                              >
                                <Chip
                                  size="small"
                                  label={`WRO · ${statuses.join('/') || 'linked'}`}
                                  color={allStowed ? 'success' : 'default'}
                                  variant="outlined"
                                  sx={{ ml: 0.5, cursor: 'help' }}
                                />
                              </Tooltip>
                              </>
                            );
                          })()}
                          {po.received === true && (() => {
                            const byShipBob = po.lineItems.filter(li => li.receivedSource === 'wro');
                            const byHand = po.lineItems.filter(li => li.receivedSource === 'manual');
                            const stowedOn = Array.from(new Set(byShipBob.map(li => li.stowedAt).filter(Boolean)));
                            return (
                              <Tooltip
                                title={
                                  <Box sx={{ whiteSpace: 'pre-line' }}>
                                    {byShipBob.length > 0
                                      ? `ShipBob stowed the full quantity on ${byShipBob.length} of ${po.lineItems.length} line(s)${stowedOn.length ? ` (${stowedOn.join(', ')})` : ''}, so those count as received automatically and cannot be unmarked here.\n`
                                      : ''}
                                    {byHand.length > 0
                                      ? `${byHand.length} line(s) were marked received by hand. Use "Mark Unreceived" to undo that.\n`
                                      : ''}
                                    {'Received units stop counting as incoming; they are already in on-hand stock.'}
                                  </Box>
                                }
                              >
                                <Chip
                                  size="small"
                                  label={byHand.length === 0 ? 'received · ShipBob' : byShipBob.length === 0 ? 'received · by hand' : 'received · mixed'}
                                  color="success"
                                  variant="outlined"
                                  sx={{ ml: 0.5, cursor: 'help' }}
                                />
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {(() => {
                            // Receiving status straight from NetSuite (see netsuiteStatus.ts);
                            // presence is determined by the lookup, not push history.
                            const drift = netsuiteDrift[po.poNumber];
                            if (netsuiteDriftError) {
                              return <Tooltip title={`Could not check NetSuite: ${netsuiteDriftError}. Click to retry.`}><Chip size="small" label="Check failed" color="warning" variant="outlined" onClick={refreshNetSuiteDrift} /></Tooltip>;
                            }
                            if (!netsuiteDriftLoaded) {
                              return <Chip size="small" label="Checking…" variant="outlined" sx={{ cursor: 'default' }} />;
                            }
                            const status: NetSuiteColumnStatus = !drift || drift.missing
                              ? 'not_in_netsuite'
                              : drift.receivingStatus ?? 'unknown';
                            const display = NETSUITE_STATUS_DISPLAY[status];
                            const linked = drift && !drift.missing ? drift : null;
                            return (
                              <Tooltip
                                title={
                                  <Box sx={{ whiteSpace: 'pre-line' }}>
                                    {drift?.missing
                                      ? `NetSuite has no record ${drift.tranId} any more (it may have been deleted).`
                                      : linked
                                        ? `${linked.tranId} · NetSuite status: ${linked.netsuiteStatus ?? 'unknown'}\n${display.help}\nClick to open it in NetSuite.`
                                        : display.help}
                                  </Box>
                                }
                              >
                                <Chip
                                  size="small"
                                  label={display.label}
                                  color={display.color}
                                  variant="outlined"
                                  {...(linked ? { component: 'a', href: linked.url, target: '_blank', rel: 'noopener', clickable: true } : {})}
                                  sx={{ cursor: linked ? 'pointer' : 'help' }}
                                />
                              </Tooltip>
                            );
                          })()}
                          {(() => {
                            const drift = netsuiteDrift[po.poNumber];
                            if (!drift || !drift.differs) return null;
                            return (
                              <Tooltip
                                title={
                                  <Box sx={{ whiteSpace: 'pre-line' }}>
                                    {drift.missing
                                      ? `NetSuite has no record ${drift.tranId} any more (it may have been deleted).`
                                      : `${drift.tranId} in NetSuite differs from this PO${drift.lastModified ? ` (NetSuite last changed ${drift.lastModified})` : ''}:\n`}
                                    {drift.diffs.map(d =>
                                      `${d.name || d.sku || d.inventoryId}: PO ${d.poQty ?? 'not on PO'}${d.poRate != null ? ` @ $${d.poRate.toFixed(2)}` : ''} · NetSuite ${d.nsQty ?? 'not in NetSuite'}${d.nsRate != null ? ` @ $${d.nsRate.toFixed(2)}` : ''}`
                                    ).join('\n')}
                                    {'\nOpen the PO and use "Update in NetSuite" to re-send it, or fix the PO to match.'}
                                  </Box>
                                }
                              >
                                <Chip size="small" label="NetSuite ≠ PO" color="error" variant="outlined" sx={{ ml: 0.5, cursor: 'help' }} />
                              </Tooltip>
                            );
                          })()}
                          {(() => {
                            const drift = netsuiteDrift[po.poNumber];
                            if (!drift || drift.missing || !drift.nameDiffers) return null;
                            return (
                              <Tooltip title={`NetSuite still calls this PO ${drift.tranId} (renamed here; it should be ${drift.expectedTranId}). Open the PO and use "Rename in NetSuite".`}>
                                <Chip size="small" label="Old name in NetSuite" color="warning" variant="outlined" sx={{ ml: 0.5, cursor: 'help' }} />
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">{po.lineItems.length}</TableCell>
                        <TableCell align="right">
                          {(() => {
                            const ordered = po.lineItems.reduce((sum, li) => sum + li.quantity, 0);
                            const incoming = po.lineItems.reduce((sum, li) => sum + (li.incomingQuantity ?? (li.received ? 0 : li.quantity)), 0);
                            if (incoming === ordered) return ordered.toLocaleString();
                            return (
                              <Tooltip title={`${ordered.toLocaleString()} ordered, ${(ordered - incoming).toLocaleString()} already stowed or received. Only the remaining ${incoming.toLocaleString()} still count as incoming in the forecast.`}>
                                <Box component="span" sx={{ cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}>
                                  {ordered.toLocaleString()}
                                </Box>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="center" sx={{ p: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'nowrap' }}>
                          {/* Payment status indicator */}
                          {(onBulkMarkAllPaid || onBulkClearPaid) && (() => {
                            const status = getPaymentStatus(po);
                            if (status === 'paid') return (
                              <Tooltip title="Fully Paid">
                                <PaidOutlinedIcon sx={{ fontSize: 18, color: 'success.main', mx: 0.25 }} />
                              </Tooltip>
                            );
                            if (status === 'partially-paid') return (
                              <Tooltip title="Partially Paid">
                                <PaidOutlinedIcon sx={{ fontSize: 18, color: 'warning.main', mx: 0.25 }} />
                              </Tooltip>
                            );
                            return (
                              <Tooltip title="Unpaid">
                                <MoneyOffCsredIcon sx={{ fontSize: 18, color: 'text.disabled', mx: 0.25 }} />
                              </Tooltip>
                            );
                          })()}
                          {/* Promote button: only on draft tab for POs with placement_date and supplier_id */}
                          {poTabValue === "draft" && onPromotePO && (
                            <Tooltip title={
                              !po.placementDate || !po.supplierId
                                ? "Placement date and supplier required to promote"
                                : "Move to Confirmed"
                            }>
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => onPromotePO(po)}
                                  disabled={!po.placementDate || !po.supplierId}
                                >
                                  <UpgradeIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {/* Mark Received button: only on confirmed tab for past-due unreceived POs */}
                          {poTabValue === "confirmed" && onMarkReceived && (
                            moment(po.arriveByDate).isSameOrBefore(today, "day") && po.received !== true ? (
                              <Tooltip title="Mark as Received">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    disabled={isMarkingReceived}
                                    onClick={async () => {
                                      setIsMarkingReceived(true);
                                      try {
                                        await onMarkReceived(po);
                                      } finally {
                                        setIsMarkingReceived(false);
                                      }
                                    }}
                                  >
                                    <CheckCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null
                          )}
                          {/* Unmark Received button: only on confirmed tab, only for a receipt a person marked */}
                          {poTabValue === "confirmed" && onUnmarkReceived && hasHandReceipt(po) && (
                            <Tooltip title="Unmark as Received">
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  disabled={isMarkingReceived}
                                  onClick={async () => {
                                    setIsMarkingReceived(true);
                                    try {
                                      await onUnmarkReceived(po);
                                    } finally {
                                      setIsMarkingReceived(false);
                                    }
                                  }}
                                >
                                  <UndoIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {onGeneratePdf && (
                            <Tooltip title="Generate PO document (PDF)">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={generatingPdfPoNumber != null}
                                  onClick={() => onGeneratePdf(po)}
                                >
                                  <PictureAsPdfIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => onOpenEditPO(po)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <Tooltip title="Duplicate: start a new PO with this one's lines, supplier and shipping details">
                            <IconButton
                              size="small"
                              onClick={() => onDuplicatePO(po)}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDeletePO(po)}
                            disabled={isDeletingPO}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                {hasActiveFilters
                  ? "No POs match the current filters."
                  : poTabValue === "incomplete"
                  ? "All POs have required cashflow fields filled in."
                  : poTabValue === "confirmed"
                  ? "No confirmed purchase orders."
                  : poTabValue === "draft"
                  ? "No forecasted orders. Click \"Create New PO\" to add one."
                  : poTabValue === "drafts"
                  ? "No drafts. A PO saved before its prices, container, ship-to and tariff are filled in lands here."
                  : poTabValue === "future"
                  ? "No upcoming purchase orders. Click \"Create New PO\" to add one."
                  : "No past purchase orders."}
              </Typography>
            )}

            {totalFilteredCount > 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <InputLabel>Per page</InputLabel>
                  <Select
                    value={itemsPerPage}
                    label="Per page"
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(0);
                    }}
                  >
                    {[10, 25, 50].map(n => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

          </>
        ) : (
          // Edit/Create view — delegated to POEditForm
          <POEditForm
            inventoryOptions={inventoryOptions}
            editingPONumber={editingPONumber}
            editingPODate={editingPODate}
            editingPODescription={editingPODescription}
            editingPOLineItems={editingPOLineItems}
            suppliers={suppliers}
            editingPOAmount={editingPOAmount}
            editingPOSupplierId={editingPOSupplierId}
            editingPOTariffRate={editingPOTariffRate}
            editingPOStatus={editingPOStatus}
            editingPOBrand={editingPOBrand}
            editingPOPlacementDate={editingPOPlacementDate}
            editingPOEtd={editingPOEtd}
            onCancelEdit={onCancelEdit}
            onDuplicate={editingPO ? onDuplicateOpenPO : undefined}
            duplicatedFromPoNumber={duplicatedFromPoNumber}
            onSetPONumber={onSetPONumber}
            onSetPODate={onSetPODate}
            onSetPODescription={onSetPODescription}
            onAddLineItem={onAddLineItem}
            onUpdateLineItem={onUpdateLineItem}
            onRemoveLineItem={onRemoveLineItem}
            onReplaceLineItems={onReplaceLineItems}
            skuQuestionCount={skuQuestions.length}
            onConfirmSkus={skuQuestions.length > 0 ? () => setConfirmSkusMode("review") : undefined}
            netsuiteDrift={editingPO ? netsuiteDrift[editingPO.poNumber] ?? null : null}
            onRefreshNetSuiteDrift={refreshNetSuiteDrift}
            savedPONumber={isCreatingNewPO ? null : editingPO?.poNumber ?? null}
            manualSavedAmount={(() => {
              if (!editingPO || editingPO.amount == null) return null;
              const savedLineTotal = getPOLineItemsTotal(editingPO.lineItems);
              return savedLineTotal == null || Math.abs(editingPO.amount - savedLineTotal) > 0.005 ? editingPO.amount : null;
            })()}
            isSaving={isSavingPOs}
            onSetPOAmount={onSetPOAmount}
            onSetPOSupplierId={onSetPOSupplierId}
            onSetPOTariffRate={onSetPOTariffRate}
            onSetPOStatus={onSetPOStatus}
            onSetPOBrand={onSetPOBrand}
            onSetPOPlacementDate={onSetPOPlacementDate}
            onSetPOEtd={onSetPOEtd}
            editingPOCountryOfDestination={editingPOCountryOfDestination}
            editingPOPaymentDateOverrides={editingPOPaymentDateOverrides}
            onSetPOCountryOfDestination={onSetPOCountryOfDestination}
            onSetPOPaymentDateOverrides={onSetPOPaymentDateOverrides}
            editingPOPriceType={editingPOPriceType}
            editingPOContainerType={editingPOContainerType}
            onSetPOPriceType={onSetPOPriceType}
            onSetPOContainerType={onSetPOContainerType}
            editingPOShipToFcId={editingPOShipToFcId}
            onSetPOShipToFcId={onSetPOShipToFcId}
            catalogSuggestions={catalogSuggestions}
            onSuggestPONumber={onSuggestPONumber}
            onGeneratePdf={onGeneratePdf && editingPO ? () => onGeneratePdf(editingPO) : undefined}
            isGeneratingPdf={generatingPdfPoNumber != null}
            poNumberError={poNumberConflict ? `PO ${editingPONumber.trim()} already exists` : null}
            editingPOPaidTranches={editingPO ? consolidatedPOs.find(p => p.poNumber === editingPO.poNumber)?.paidTranches : undefined}
            wroOptions={wroOptions}
            linkedWroIds={editingPO
              ? Array.from(new Set(
                  (consolidatedPOs.find(p => p.poNumber === editingPO.poNumber)?.lineItems ?? []).flatMap(li => li.wroIds ?? []),
                ))
              : []}
            pinnedWroId={editingPO ? consolidatedPOs.find(p => p.poNumber === editingPO.poNumber)?.pinnedWroId ?? null : null}
            wroLinkSource={editingPO ? consolidatedPOs.find(p => p.poNumber === editingPO.poNumber)?.wroLinkSource ?? 'auto' : 'auto'}
            onLinkWRO={onLinkWRO && editingPO ? (link) => onLinkWRO(editingPO.poNumber, link) : undefined}
            onSetWroAllocations={onSetWroAllocations && editingPO ? (request) => onSetWroAllocations(editingPO.poNumber, request) : undefined}
            onSetWroLineAllocations={onSetWroLineAllocations}
            onSuppliersChanged={onSuppliersChanged}
            poOptions={consolidatedPOs
              .filter(po => po.poNumber !== editingPO?.poNumber)
              .map(po => ({ poNumber: po.poNumber, inventoryIds: po.lineItems.filter(li => li.quantity > 0).map(li => String(li.inventoryId)) }))}
            wroLinksByInventoryId={(() => {
              const map: Record<string, WroLineLink[]> = {};
              for (const li of consolidatedPOs.find(p => p.poNumber === editingPO?.poNumber)?.lineItems ?? []) {
                if (li.wroLinks) map[String(li.inventoryId)] = li.wroLinks;
              }
              return map;
            })()}
            orphanedAllocationsByInventoryId={(() => {
              const map: Record<string, OrphanedAllocation[]> = {};
              for (const li of consolidatedPOs.find(p => p.poNumber === editingPO?.poNumber)?.lineItems ?? []) {
                if (li.orphanedAllocations?.length) map[String(li.inventoryId)] = li.orphanedAllocations;
              }
              return map;
            })()}
            onWroCreated={editingPO ? onWroCreated : undefined}
            onNetSuitePushed={editingPO ? onNetSuitePushed : undefined}
            netsuiteCanUpdate={!!(editingPO && consolidatedPOs.find(p => p.poNumber === editingPO.poNumber)?.netsuitePoId)}
            netsuiteLink={(() => {
              const current = editingPO ? consolidatedPOs.find(p => p.poNumber === editingPO.poNumber) : undefined;
              const found = current ? netsuiteDrift[current.poNumber] : null;
              if (found && !found.missing) return { tranId: found.tranId, url: found.url };
              return current?.netsuitePoId && current.netsuiteTranId
                ? { tranId: current.netsuiteTranId, url: `https://9703688.app.netsuite.com/app/accounting/transactions/purchord.nl?id=${current.netsuitePoId}` }
                : null;
            })()}
            onTogglePaidTranche={onTogglePaidTranche && editingPO ? (trancheOrder, paid) => {
              onTogglePaidTranche(editingPO.poNumber, trancheOrder, paid);
            } : undefined}
            isUpdatingPaidStatus={isUpdatingPaidStatus}
          />
        )}
      </DialogContent>
      <DialogActions>
        {!editingPO && !isCreatingNewPO ? (
          standalone ? null : <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onCancelEdit} disabled={isSavingPOs}>
              Cancel
            </Button>
            {isDraft ? (
              <>
                <Button variant="outlined" onClick={handleSaveClick} disabled={isSavingPOs || !isFormValid}>
                  {isSavingPOs ? "Saving..." : "Save draft"}
                </Button>
                <Tooltip title={editorMissing.length > 0
                  ? `Still missing: ${editorMissing.join(', ')}`
                  : "Everything required is filled in — saves this PO as placed so it counts in the forecast and cashflow"}>
                  <span>
                    <Button
                      variant="contained"
                      onClick={() => { if (skuQuestions.length > 0) { onSetPOStatus?.('placed'); setConfirmSkusMode("save"); } else saveWithStatus('placed'); }}
                      disabled={isSavingPOs || !isFormValid || editorMissing.length > 0}
                    >
                      Save as placed
                    </Button>
                  </span>
                </Tooltip>
              </>
            ) : (
              <>
                {editorMissing.length > 0 && onSetPOStatus && (
                  <Tooltip title="Keeps what you entered without counting it anywhere (not in the forecast, cashflow, or NetSuite) until the missing fields are filled in">
                    <Button variant="outlined" onClick={() => saveWithStatus('draft')} disabled={isSavingPOs || !isFormValid}>
                      Save as draft
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title={editorMissing.length > 0 ? `Fill in ${editorMissing.join(', ')} first, or save as a draft` : ""}>
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleSaveClick}
                      disabled={isSavingPOs || !isFormValid || editorMissing.length > 0}
                    >
                      {isSavingPOs ? "Saving..." : skuQuestions.length > 0 ? "Confirm SKUs & Save" : "Save PO"}
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </>
        )}
      </DialogActions>
      {onStartCreatePOFromPdf && (
        <ImportPoPdfDialog
          open={pdfImport.open}
          newPo
          poNumber="new"
          initialFile={pdfImport.file}
          inventoryOptions={inventoryOptions}
          currentLines={[]}
          catalogPriceFor={() => undefined}
          suggestedProductNumberFor={(inventoryId) => (inventoryId ? catalogSuggestions?.manufacturerSkus?.[inventoryId] : undefined)}
          onClose={() => setPdfImport({ open: false, file: null })}
          onApply={(lines, { poNumber, poDate }) => {
            // Every line was picked or confirmed by the person in the dialog
            onStartCreatePOFromPdf({ poNumber, poDate, lineItems: lines.map(li => ({ ...li, skuConfirmed: true })) });
          }}
        />
      )}
      <ConfirmSkusDialog
        open={confirmSkusMode !== null}
        questions={skuQuestions}
        continueLabel={confirmSkusMode === "save" ? "Confirm & Save PO" : "Confirm"}
        onCancel={() => setConfirmSkusMode(null)}
        onConfirm={(choices) => {
          applySkuChoices(choices);
          const save = confirmSkusMode === "save";
          setConfirmSkusMode(null);
          if (save) setSaveAfterConfirm(true);
        }}
      />
    </>
  );

  if (standalone) {
    return <Paper variant="outlined">{dialogInner}</Paper>;
  }

  return (
    <Dialog
      open={isOpen}
      // Not while a save is in flight: the editor must still be on that PO when it finishes
      onClose={() => { if (!isSavingPOs) onClose(); }}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { minHeight: 500 } }}
    >
      {dialogInner}
    </Dialog>
  );
}
